import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button, Col, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../Api/ApiMethods';
import PropTypes from 'prop-types'; // Import PropTypes
import FilesComponent from '../CallForms/Component/FilesComponent'; // Import FilesComponent

// Helper function to format date for datetime-local input
const toLocalISOString = (date) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';
        const tzOffset = dateObj.getTimezoneOffset() * 60000;
        const localDate = new Date(dateObj.getTime() - tzOffset);
        return localDate.toISOString().slice(0, 16);
    } catch (error) {
        return '';
    }
};

// Define a constant empty array outside the component
const EMPTY_ARRAY = [];

const JobDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedJob, refreshData }) => {
    // Define initial state structure, ensuring phone is always an array of two objects
    const initialJobData = {
        id: '',
        plate: '',
        chassisNumber: '',
        accidentDate: '', // Will be formatted for input
        reportDate: '', // Will be formatted for input
        tcid: '',
        fullName: '',
        phone: [{ phone: '' }, { phone: '' }], // Array for two phone numbers
        faultStatus: '',
        reference: '', // <-- Added reference
        originFolder: '', // <-- Added originFolder
    };    
    
    const [jobData, setJobData] = useState(initialJobData);
    const [errors, setErrors] = useState({});
    // selectedFiles state removed - FilesComponent handles its own file management
    const [existingFiles, setExistingFiles] = useState(EMPTY_ARRAY); // State for existing files in modify mode
    const [currentJobId, setCurrentJobId] = useState(null); // State for current job ID (for file uploads)

    // Effect to load data or reset form when modal opens or mode/data changes
    useEffect(() => {
        if (isOpen) { // Run only when modal is open
            if (mode === 'modify' && selectedJob) {
                // Ensure phone data structure is maintained
                const phones = Array.isArray(selectedJob.phone) ? selectedJob.phone : [];
                const formPhones = [
                    phones[0] || { phone: '' }, // First phone or empty object
                    phones[1] || { phone: '' }, // Second phone or empty object
                ];                // Format dates for datetime-local input
                const formattedAccidentDate = toLocalISOString(selectedJob.accidentDate);
                const formattedReportDate = toLocalISOString(selectedJob.reportDate);

                // Set form data merging initial structure with selected data
                setJobData({
                    ...initialJobData,
                    ...selectedJob,
                    accidentDate: formattedAccidentDate,
                    reportDate: formattedReportDate,
                    phone: formPhones, // Use processed phone array
                     // Ensure numeric fields are strings for input consistency
                     tcid: String(selectedJob.tcid || ''),
                     faultStatus: selectedJob.faultStatus !== null && selectedJob.faultStatus !== undefined ? String(selectedJob.faultStatus) : '',
                     reference: selectedJob.reference || '', // <-- Load reference
                     originFolder: selectedJob.originFolder || '', // <-- Load originFolder
                });
                // Set current job ID for file uploads
                setCurrentJobId(selectedJob.id);
                 // Load existing files if available
                 setExistingFiles(selectedJob.files || EMPTY_ARRAY);            
                } else {
                // Add mode: reset to initial state
                resetForm();
                setCurrentJobId(null); // No job ID yet for new records
            }
             // Clear errors when modal opens or mode changes
             setErrors({});
        }
         // Cleanup existing files when modal closes
        else {
            setExistingFiles(EMPTY_ARRAY);
        }
    }, [mode, selectedJob, isOpen]); // Dependencies    // Reset form to initial state
    const resetForm = () => {
        setJobData(initialJobData);
        setErrors({});
        // selectedFiles state removed - FilesComponent handles its own file management
        setExistingFiles(EMPTY_ARRAY); // Reset existing files
        setCurrentJobId(null); // Reset current job ID
    };

    // Universal input change handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // Fields for standard uppercase (remove plate from here)
        const fieldsStandardUppercase = ['chassisNumber'];
        // Field for Turkish locale uppercase
        const fieldsTurkishUppercase = ['fullName', 'reference']; // <-- Added reference

        // Apply transformations
        if (name === 'plate' && typeof processedValue === 'string') {
            // Apply Turkish locale uppercase for plate, keep spaces
            processedValue = processedValue.toLocaleUpperCase('tr-TR');
        } else if (fieldsStandardUppercase.includes(name) && typeof processedValue === 'string') {
            // Apply standard uppercase and remove spaces for chassisNumber
            processedValue = processedValue.toUpperCase().replace(/\s/g, '');
        } else if (fieldsTurkishUppercase.includes(name) && typeof processedValue === 'string') {
            // Apply Turkish locale uppercase for fullName
            processedValue = processedValue.toLocaleUpperCase('tr-TR');
        }
        // TC ID: Keep only digits, max 11
        else if (name === 'tcid' && typeof processedValue === 'string') {
             processedValue = processedValue.replace(/[^0-9]/g, '');
             if (processedValue.length > 11) {
                 processedValue = processedValue.slice(0, 11);
             }
        }
         // Fault Status: Keep only digits, handle range in validation/save
         else if (name === 'faultStatus' && typeof processedValue === 'string') {
             processedValue = processedValue.replace(/[^0-9]/g, '');
         }

        // Update state
        setJobData(prevData => ({ ...prevData, [name]: processedValue }));

        // Clear validation error for the field being changed
        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        }
    };


    // Handler specifically for the fixed phone inputs
    const handlePhoneChange = (index, value) => {
        // Keep only digits, limit length (e.g., 11 for TR)
        let processedValue = value.replace(/[^0-9]/g, '');
        if (processedValue.length > 11) {
            processedValue = processedValue.slice(0, 11);
        }

        // Create a copy and update the specific phone object
        const updatedPhones = [...jobData.phone];
        updatedPhones[index] = { phone: processedValue };

        // Update state
        setJobData(prevData => ({ ...prevData, phone: updatedPhones }));

        // Clear potential phone-related errors for the specific index
        const errorKey = `phone_${index}`;
        if (errors[errorKey]) {
            setErrors(prevErrors => ({ ...prevErrors, [errorKey]: '' }));
        }
    };

    // Callback for FilesComponent updates - no longer needed as FilesComponent handles its own management
    const handleFileSelectionChange = useCallback((filesData) => {
        // FilesComponent handles file management internally, no need to store in state
        console.log('FilesComponent files changed:', filesData);
    }, []);

    // Form validation logic
    const validateForm = () => {
        const validationErrors = {};
        const tcidRegex = /^\d{11}$/;
        const phoneRegex = /^5\d{9}$/;
        // Remove the specific plate regex
        // const plateRegex = /^\d{2}[A-Z]{1,3}\d{2,4}$/;
        const chassisRegex = /^[A-HJ-NPR-Z0-9]{17}$/; // Standard 17-char VIN

        // --- Required Fields ---
        if (!jobData.plate?.trim()) {
            validationErrors.plate = 'Plaka zorunludur.';
        }
        // Remove the regex test for plate format
        // else if (!plateRegex.test(jobData.plate)) {
        //     validationErrors.plate = 'Geçerli bir plaka formatı giriniz (örn: 34ABC123).';
        // }

        if (!jobData.chassisNumber?.trim()) {
            validationErrors.chassisNumber = 'Şase Numarası zorunludur.';
        } else if (!chassisRegex.test(jobData.chassisNumber)) {
             validationErrors.chassisNumber = 'Geçerli bir 17 haneli şase numarası girin (I, O, Q hariç).';
        }

        if (!jobData.accidentDate) validationErrors.accidentDate = 'Kaza Tarihi zorunludur.';
        if (!jobData.reportDate) validationErrors.reportDate = 'Rapor Tarihi zorunludur.';

        if (!jobData.tcid?.trim()) {
            validationErrors.tcid = 'TC Kimlik Numarası zorunludur.';
        } else if (!tcidRegex.test(jobData.tcid)) {
            validationErrors.tcid = 'TC Kimlik Numarası 11 haneli rakam olmalıdır.';
        }

        if (!jobData.fullName?.trim()) {
            validationErrors.fullName = 'Ad Soyad zorunludur.';
        } else if (jobData.fullName.trim().length < 2) {
             validationErrors.fullName = 'Ad Soyad en az 2 karakter olmalıdır.';
        }

        // Phone 1 validation (Required)
        const phone1Raw = jobData.phone[0]?.phone?.trim() || '';
        if (!phone1Raw) {
            validationErrors.phone_0 = 'Telefon 1 zorunludur.';
        } else {
            // Test against the 10-digit format (remove leading 0 for test if present)
            const phone1ToTest = phone1Raw.startsWith('0') ? phone1Raw.substring(1) : phone1Raw;
            if (!phoneRegex.test(phone1ToTest)) {
                // Update error message
                validationErrors.phone_0 = 'Telefon 1, 5 ile başlamalı ve 10 haneli olmalıdır.';
            }
        }

        // Phone 2 validation (Optional, but validated if present)
        const phone2Raw = jobData.phone[1]?.phone?.trim() || '';
        if (phone2Raw) {
             // Test against the 10-digit format (remove leading 0 for test if present)
            const phone2ToTest = phone2Raw.startsWith('0') ? phone2Raw.substring(1) : phone2Raw;
            if (!phoneRegex.test(phone2ToTest)) {
                 // Update error message
                validationErrors.phone_1 = 'Telefon 2, 5 ile başlamalı ve 10 haneli olmalıdır.';
            }
        }

        // Fault Status validation (Required, 0-100)
        const faultStatusValue = jobData.faultStatus; // Already string of digits or empty
        if (faultStatusValue === '' || faultStatusValue === null) {
             validationErrors.faultStatus = 'Kusur Durumu zorunludur.';
        } else {
             const faultNum = Number(faultStatusValue);
             if (isNaN(faultNum) || faultNum < 0 || faultNum > 100) {
                 validationErrors.faultStatus = 'Kusur Durumu 0 ile 100 arasında bir sayı olmalıdır.';
             }
        }

        // Add validation for reference (optional, example: min length)
        if (jobData.reference?.trim() && jobData.reference.trim().length < 2) {
             validationErrors.reference = 'Referans adı en az 2 karakter olmalıdır.';
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0; // True if no errors
    };

    // Handle modal close/cancel action
    const handleCancel = () => {
        resetForm(); // Reset form state
        toggle(); // Close the modal
    };

    // Handle form submission (Add or Modify)
    const handleOperation = async () => {
        if (!validateForm()) { // Validate first
            toast.error('Lütfen formdaki hataları düzeltin.');
            const firstErrorKey = Object.keys(errors).find(key => errors[key]);
            if (firstErrorKey) document.getElementById(firstErrorKey)?.focus();
            return;
        }

        // Prepare data for API
        const accidentDateToSend = jobData.accidentDate ? new Date(jobData.accidentDate).toISOString() : null;
        const reportDateToSend = jobData.reportDate ? new Date(jobData.reportDate).toISOString() : null;

        // Determine API endpoint
        let targetApiEndpoint = apiEndpoint; // Base endpoint for modify
        if (mode === 'add') {
            targetApiEndpoint = `${apiEndpoint}/manual`; // Use /manual endpoint for add
        }

        // File handling removed - FilesComponent handles its own uploads via /UploadedFile endpoint
        // Prepare JSON payload
        const phonesToSend = jobData.phone
            .map(p => {
                let phoneNum = p.phone?.trim() || '';
                // Telefon numarası 0 ile başlıyorsa ve 11 haneli ise, 0'ı kaldırMA
                // Backend'e tam numarayı gönder
                return { phone: phoneNum };
            })
            .filter(p => p.phone);

        const dataToSend = {
            plate: jobData.plate,
            chassisNumber: jobData.chassisNumber,
            accidentDate: accidentDateToSend,
            reportDate: reportDateToSend,
            tcid: jobData.tcid,
            fullName: jobData.fullName,
            phone: phonesToSend,
            faultStatus: Number(jobData.faultStatus),
            reference: jobData.reference || null,
            originFolder: jobData.originFolder || null,
        };

        if (mode === 'modify' && selectedJob?.id) {
            dataToSend.id = selectedJob.id;
        }

        console.log(`Sending JSON Payload (${mode} to ${targetApiEndpoint}):`, dataToSend);        try {
            const result = await handleAddModifyOperation({
                apiEndpoint: targetApiEndpoint,
                selectedItem: mode === 'modify' ? { id: selectedJob?.id } : null,
                mode,
                data: dataToSend, // Send JSON data
                refreshDataCallback: refreshData,
                componentName: 'İş Kaydı',
                isFormData: false // Always JSON now that FilesComponent handles file uploads
            });
            
            // If adding a new job and we got a result with ID, update currentJobId for file uploads
            if (mode === 'add' && result && result.id) {
                setCurrentJobId(result.id);
                toast.success('İş kaydı oluşturuldu. Artık dosya yükleyebilirsiniz.');
                // Don't close modal immediately, let user upload files via FilesComponent
                return; // Don't close modal yet
            }
            
            toggle(); // Close modal on success (modify mode or add without files)
        } catch (error) {
            console.error("İş Kaydı operasyonu başarısız:", error);
            // Error toast is likely handled within handleAddModifyOperation
        }
    };

    // Helper functions for title and button label
    const getModalTitle = () => (mode === 'add' ? 'Manuel İş Kaydı Ekle' : 'İş Kaydı Düzenle'); // Adjusted title for clarity
    const getButtonLabel = () => (mode === 'add' ? 'Ekle' : 'Güncelle');

    return (
        // Use handleCancel for the main toggle to ensure reset
        <Modal isOpen={isOpen} toggle={handleCancel} size="lg" backdrop="static">
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    {/* Plate & Chassis */}
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="plate">Plaka <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="plate"
                                    id="plate"
                                    value={jobData.plate || ''}
                                    onChange={handleInputChange} // Use unified handler
                                    placeholder="Örn: 34ABC123"
                                    invalid={!!errors.plate}
                                />
                                <FormFeedback>{errors.plate}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="chassisNumber">Şase Numarası <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="chassisNumber"
                                    id="chassisNumber"
                                    value={jobData.chassisNumber || ''}
                                    onChange={handleInputChange} // Use unified handler
                                    placeholder="17 Haneli Şase Numarası"
                                    maxLength={17}
                                    invalid={!!errors.chassisNumber}
                                />
                                <FormFeedback>{errors.chassisNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Dates */}
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="accidentDate">Kaza Tarihi <span className="text-danger">*</span></Label>
                                <Input
                                    type="datetime-local"
                                    name="accidentDate"
                                    id="accidentDate"
                                    value={jobData.accidentDate || ''} // Needs YYYY-MM-DDTHH:mm
                                    onChange={handleInputChange} // Use unified handler
                                    invalid={!!errors.accidentDate}
                                />
                                <FormFeedback>{errors.accidentDate}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="reportDate">Rapor Tarihi <span className="text-danger">*</span></Label>
                                <Input
                                    type="datetime-local"
                                    name="reportDate"
                                    id="reportDate"
                                    value={jobData.reportDate || ''} // Needs YYYY-MM-DDTHH:mm
                                    onChange={handleInputChange} // Use unified handler
                                    invalid={!!errors.reportDate}
                                />
                                <FormFeedback>{errors.reportDate}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* TCID & FullName */}
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="tcid">TC Kimlik Numarası <span className="text-danger">*</span></Label>
                                <Input
                                    type="text" // Keep as text for length control
                                    name="tcid"
                                    id="tcid"
                                    value={jobData.tcid || ''}
                                    onChange={handleInputChange} // Use unified handler
                                    placeholder="11 Haneli TC Kimlik No"
                                    maxLength={11} // HTML5 limit
                                    invalid={!!errors.tcid}
                                />
                                <FormFeedback>{errors.tcid}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="fullName">Ad Soyad <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    name="fullName"
                                    id="fullName"
                                    value={jobData.fullName || ''}
                                    onChange={handleInputChange} // Use unified handler
                                    placeholder="Müşteri Adı Soyadı"
                                    invalid={!!errors.fullName}
                                />
                                <FormFeedback>{errors.fullName}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* --- Added Reference Field --- */}
                    <FormGroup>
                        <Label for="reference">Kimin Referansı?</Label>
                        <Input
                            type="text"
                            name="reference"
                            id="reference"
                            value={jobData.reference || ''}
                            onChange={handleInputChange} // Use unified handler
                            placeholder="Referans Adı Soyadı (Opsiyonel)"
                            invalid={!!errors.reference}
                        />
                        <FormFeedback>{errors.reference}</FormFeedback>
                    </FormGroup>
                    {/* --- End Added Reference Field --- */}

                    {/* --- Fixed Phone Inputs --- */}
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="phone_0">Telefon 1 <span className="text-danger">*</span></Label>
                                <Input
                                    type="tel" // Use tel type
                                    id="phone_0"
                                    name="phone_0"
                                    value={jobData.phone[0]?.phone || ''} // Access first phone object
                                    onChange={(e) => handlePhoneChange(0, e.target.value)} // Use specific handler
                                    placeholder="05XX XXX XX XX"
                                    maxLength={11} // HTML5 limit
                                    invalid={!!errors.phone_0} // Use specific error key
                                />
                                <FormFeedback>{errors.phone_0}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="phone_1">Telefon 2</Label>
                                <Input
                                    type="tel" // Use tel type
                                    id="phone_1"
                                    name="phone_1"
                                    value={jobData.phone[1]?.phone || ''} // Access second phone object
                                    onChange={(e) => handlePhoneChange(1, e.target.value)} // Use specific handler
                                    placeholder="05XX XXX XX XX (Opsiyonel)"
                                    maxLength={11} // HTML5 limit
                                    invalid={!!errors.phone_1} // Use specific error key
                                />
                                <FormFeedback>{errors.phone_1}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    {/* --- End Fixed Phone Inputs --- */}

                     {/* Kusur Durumu */}
                    <FormGroup>
                        <Label for="faultStatus">Kusur Durumu (%) <span className="text-danger">*</span></Label>
                        <Input
                            type="number" // Use number type, browser might add spinners
                            name="faultStatus"
                            id="faultStatus"
                            value={jobData.faultStatus || ''}
                            onChange={handleInputChange} // Use unified handler
                            placeholder="0-100 Arası Yüzde Değeri"
                            invalid={!!errors.faultStatus}
                            min="0" // HTML5 validation attributes
                            max="100"
                        />
                        <FormFeedback>{errors.faultStatus}</FormFeedback>
                    </FormGroup>

                    {/* Origin Folder - Read Only */}
                    <FormGroup>
                        <Label for="originFolder">Kaynak Klasör</Label>
                        <Input
                            type="text"
                            name="originFolder"
                            id="originFolder"
                            value={jobData.originFolder || ''}
                            disabled={true} // Always disabled - not editable
                            placeholder="Yüklenen klasör ismi"
                        />
                    </FormGroup>

                    {/* --- File Upload Section --- */}
                    <hr className="mt-4 mb-4"/>
                    <FormGroup>                         
                        <FilesComponent
                             onFilesChange={() => {}} // FilesComponent handles file management internally
                             existingFiles={existingFiles} // Pass existing files for display/deletion
                             multiple={true} // Allow multiple file uploads for this record
                             callFormReference={false} // Bu JobPool için, çağrı formu değil
                             referenceId={currentJobId} // Current job ID (null for new records)
                             typeFilter={null} // Tüm dosya türlerine izin ver
                             title="İş Belgeleri" // Özel başlık
                             canEdit={true}
                         />
                    </FormGroup>
                    {/* --- End File Upload Section --- */}

                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleOperation}>{getButtonLabel()}</Button>
                <Button color="secondary" onClick={handleCancel} className="ms-2">İptal</Button>
            </ModalFooter>
        </Modal>
    );
};

// Add PropTypes for component props
JobDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedJob: PropTypes.object, // Can be null/undefined in 'add' mode
    refreshData: PropTypes.func.isRequired,
};


export default JobDataModal; 